import React, { useMemo, useState } from 'react'

import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { ArrowPathIcon } from '@heroicons/react/24/outline'

import { AddNewButton } from '../components/AddNewButton'
import { NewCustomerMessage } from './NewCustomerMessage'
import { taskLabelsLookup } from './helper'
import Message from '@/components/Message/Message'
import PropertyFlag from '@/components/PropertyFlag/PropertyFlag'
import { useCurrentApplication } from '@/services/application'
import { useGetMessagesQuery } from '@/services/message'
import { useListPropertyFlagsQuery } from '@/services/propertyFlag'
import {
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
} from '@/services/v2-application/applicationProfile'
import { CustomerTask, IMessage, IPropertyFlag } from '@/types/unlock'
import { formatDateTime } from '@/utils/date'

export function CustomerMessages() {
  const [isNewMessageDrawerOpen, setIsNewMessageDrawerOpen] = useState(false)

  const {
    data: application,
    isSuccess: isSuccessApp,
    isLoading: isLoadingApp,
  } = useCurrentApplication()

  const {
    data: messages,
    isSuccess: isSuccessMessages,
    refetch,
  } = useGetMessagesQuery(application?.external_id, {
    skip: !application?.external_id,
  })

  const { data: propertyFlags } = useListPropertyFlagsQuery(application?.external_id, {
    skip: !application?.external_id,
  })

  const { data: applicationProfiles, isSuccess: isSuccessApplicationProfiles } =
    useListApplicationProfilesQuery(application?.uuid, {
      skip: !application?.uuid,
    })

  const { data: nonApplicants, isSuccess: isSuccessNonApplicants } = useListNonApplicantsQuery(
    application?.uuid,
    {
      skip: !application?.uuid,
    }
  )

  const aggregatedData = useMemo(() => {
    let result: Array<
      | { type: 'message'; obj: IMessage }
      | { type: 'task'; obj: CustomerTask }
      | { type: 'propertyFlag'; obj: IPropertyFlag }
    > = []

    if (messages != undefined) {
      result = result.concat(
        messages.map((it) => ({
          type: 'message',
          obj: it,
        }))
      )
    }

    if (propertyFlags != undefined) {
      result = result.concat(
        propertyFlags.map((it) => ({
          type: 'propertyFlag',
          obj: it,
        }))
      )
    }

    return result.sort((a, b) => {
      return Date.parse(a.obj.created_at) - Date.parse(b.obj.created_at)
    })
  }, [messages, propertyFlags])

  return (
    <div className='p-3'>
      {!isLoadingApp && !application?.id && <p>No application selected</p>}

      <div className='flex items-center justify-end gap-3 text-center w-full'>
        <button
          type='button'
          onClick={refetch}
        >
          <ArrowPathIcon
            width={24}
            height={24}
          />
        </button>
        {isSuccessApp && isSuccessMessages && (
          <AddNewButton onClick={() => setIsNewMessageDrawerOpen(true)}>Add New</AddNewButton>
        )}
      </div>

      <NewCustomerMessage
        isOpen={isNewMessageDrawerOpen}
        setIsOpen={setIsNewMessageDrawerOpen}
      />

      <div className='flex flex-col gap-2 pt-3 pb-3'>
        {aggregatedData.map((item) => {
          if (item.type === 'message') {
            const message = item.obj as IMessage

            return (
              <div key={`${message.id}`}>
                {isSuccessApplicationProfiles && isSuccessNonApplicants && (
                  <Message
                    applicants={applicationProfiles?.results}
                    nonApplicants={nonApplicants?.results}
                    {...message}
                  />
                )}
              </div>
            )
          } else if (item.type === 'propertyFlag') {
            const propertyFlag = item.obj as IPropertyFlag

            return (
              <div
                key={`${propertyFlag.uuid}`}
                className='py-1'
              >
                <PropertyFlag propertyFlag={propertyFlag} />
              </div>
            )
          } else {
            const task = item.obj as CustomerTask

            return (
              <div
                key={task.uuid}
                className='py-1'
              >
                <div className='flex py-1 gap-2 items-start'>
                  <ClipboardDocumentListIcon
                    width={24}
                    color='grey'
                  />
                  <p className='text-sm'>{taskLabelsLookup[task.name] || task.name}</p>
                </div>

                <div className='flex text-xs justify-between py-1 font-bold'>
                  <p>
                    {
                      applicationProfiles?.results.find(
                        (it) => it.uuid === task.application_profile_uuid
                      )?.profile.first_name
                    }
                  </p>
                  <p>
                    {formatDateTime(new Date(task.created_at))}
                    {' | '}
                    {task.status}
                  </p>
                </div>
                <hr />
              </div>
            )
          }
        })}
      </div>
    </div>
  )
}
